import { useState } from 'react';
import {
    PersonneMorale,
    reassignParrainages,
} from '@app/api/data/personne-morales';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Button,
    Card,
    Typography,
    Sheet,
    Box,
    Snackbar,
    Textarea,
    FormControl,
    FormLabel,
    FormHelperText,
} from '@mui/joy';
import { AppModal } from '@app/components/AppModal';
import AppLoading from '@app/components/AppLoading';

interface ReassignParrainagesProps {
    personneMorale?: PersonneMorale;
}

const ReassignParrainages: React.FC<ReassignParrainagesProps> = ({
    personneMorale,
}) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [factures, setFactures] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openModal, setOpenModal] = useState(false);

    const handleFacturesChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        setFactures(event.target.value);
    };

    const handleReassign = async () => {
        if (!id || !factures.trim()) return;

        setLoading(true);
        try {
            // Récupérer la liste des numéros de facture à partir du texte entré
            const factureNumbers = factures
                .split(/[ ,\t\n\r]+/) // Split par virgules, espaces, tabulations ou sauts de ligne
                .map((f) => f.trim())
                .filter((f) => f !== '');

            if (factureNumbers.length === 0) {
                setError(
                    'Veuillez entrer au moins un numéro de facture valide'
                );
                setLoading(false);
                return;
            }

            console.log(factureNumbers);
            // Réaffecter les parrainages trouvés
            await reassignParrainages(factureNumbers, parseInt(id));

            setSuccess(true);
            setOpenModal(false);
            setFactures('');

            // Recharger la page pour afficher les changements
            setTimeout(() => {
                navigate(0);
            }, 2000);
        } catch (err) {
            setError('Erreur lors de la réaffectation des parrainages');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const modalContent = (
        <>
            {loading ? (
                <Sheet className="flex justify-center py-4">
                    <AppLoading />
                </Sheet>
            ) : (
                <FormControl className="w-full">
                    <FormLabel>Parrainages à réaffecter</FormLabel>
                    <Textarea
                        placeholder="Numéros de facture (***, ***, ***)"
                        minRows={4}
                        value={factures}
                        onChange={handleFacturesChange}
                        className="mb-2 w-full"
                    />
                    <FormHelperText>
                        Les parrainages seront réaffectés à{' '}
                        {personneMorale?.raisonSociale}
                    </FormHelperText>
                </FormControl>
            )}
        </>
    );

    return (
        <Card variant="outlined" className="mt-3">
            <Box className="mb-2 flex items-center justify-between">
                <Typography level="title-md">
                    Réaffecter des parrainages
                </Typography>
                <Button
                    size="sm"
                    color="primary"
                    onClick={() => setOpenModal(true)}
                    disabled={loading || !personneMorale}
                >
                    Réaffecter des parrainages
                </Button>
            </Box>

            <AppModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                title={`Réaffecter des parrainages à ${personneMorale?.raisonSociale}`}
                icon="ArrowPathIcon"
                submitText="Réaffecter"
                submitIcon="ArrowPathIcon"
                handleSubmit={handleReassign}
                disabelSubmit={loading || factures.trim() === ''}
                closeOnSubmit={false}
                footer={<></>}
            >
                {modalContent}
            </AppModal>

            <Snackbar
                open={success}
                variant="soft"
                color="success"
                autoHideDuration={5000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                Parrainages réaffectés avec succès !
            </Snackbar>

            <Snackbar
                open={!!error}
                variant="solid"
                color="danger"
                autoHideDuration={5000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {error}
            </Snackbar>
        </Card>
    );
};

export default ReassignParrainages;
